import React, { useState } from 'react';
import { ProcessedNumericFact } from '../../hooks/useUserFactsDashboard';
import { NumericFactChart } from './NumericFactChart';
import { cn } from '../../utils/cn';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getMetricConfig } from '../../utils/wellbeingDataProcessor';

interface NumericFactDetailModalProps {
  fact: ProcessedNumericFact;
  isOpen: boolean;
  onClose: () => void;
}

export const NumericFactDetailModal: React.FC<NumericFactDetailModalProps> = ({
  fact,
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  const metricConfig = getMetricConfig(fact.key);
  const invertedScale = metricConfig?.invertedScale ?? false;

  const TrendIcon = fact.trend === 'up' 
    ? TrendingUp 
    : fact.trend === 'down' 
      ? TrendingDown 
      : Minus;

  // Sentiment-aware color: green for positive direction, red for negative
  const isPositiveDirection = invertedScale
    ? fact.trend === 'down'
    : fact.trend === 'up';
  const isNeutral = fact.trend === 'stable';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-card shadow-card max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-rm-neutral-600/10">
            <div>
              <h2 className="text-xl font-semibold text-rm-primary-700">
                {fact.displayName}
              </h2>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="text-2xl font-bold text-rm-ink-900">
                  {fact.currentValue}
                </span>
                {fact.history.length >= 2 && (
                  <>
                    <span className="text-base text-rm-neutral-600" title="Period average">
                      avg {fact.averageValue}
                    </span>
                    <div className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', 
                      isNeutral ? 'text-rm-neutral-600 bg-rm-bg-50' : isPositiveDirection ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                    )}>
                      <TrendIcon className="w-3 h-3" />
                      <span>{fact.trendLabel}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-rm-bg-50 text-rm-neutral-600 hover:text-rm-ink-900 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Full Chart */}
            <NumericFactChart fact={fact} compact={false} showHeader={false} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component for clickable charts
interface ClickableNumericFactChartProps {
  fact: ProcessedNumericFact;
  compact?: boolean;
  className?: string;
}

export const ClickableNumericFactChart: React.FC<ClickableNumericFactChartProps> = ({
  fact,
  compact = true,
  className
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div 
        onClick={() => setIsModalOpen(true)}
        className="cursor-pointer hover:ring-2 hover:ring-rm-accent-500/30 rounded-card transition-all"
      >
        <NumericFactChart fact={fact} compact={compact} className={className} />
      </div>
      
      <NumericFactDetailModal
        fact={fact}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default NumericFactDetailModal;
